import React, { useCallback, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  FileText,
  Loader2,
  PackageCheck,
  RefreshCw,
  MessageCircle,
} from "lucide-react";
import { toast } from "react-toastify";

import {
  getOrderById,
  getOrderByNumber,
  resetOrderDetails,
  resetOrderError,
  resetOrderMeta,
} from "../../actions/orderActions";
import Meta from "../../components/Meta";
import {
  formatMoney,
  getCustomerName,
  getLineMeta,
  getLineQuantity,
  getLineTitle,
  getOrderCurrency,
  getOrderItems,
  getOrderNumber,
  getOrderStatusLabel,
  getOrderStatusTone,
  getPaymentStatusLabel,
  getPaymentStatusTone,
  getFulfillmentStatusLabel,
  getFulfillmentStatusTone,
  getPaymentLabel,
  getPrimaryAddress,
  humanize,
  safeText,
} from "./orderViewUtils";
import { getSafeUserMessage } from "../../utils/errorMessages";

import "./OrderScreen.css";
import CustomerOrderActions from "./CustomerOrderActions";
import OrderTimeline from "./OrderTimeline";
import BuyAgainButton from "./BuyAgainButton";

function isObjectId(value = "") {
  return /^[a-f\d]{24}$/i.test(safeText(value));
}

function StatusBadge({ value, fallback = "Pending", type = "order" }) {
  const label =
    type === "payment"
      ? getPaymentStatusLabel(value)
      : type === "fulfillment"
        ? getFulfillmentStatusLabel(value)
        : getOrderStatusLabel(value) || fallback;

  const tone =
    type === "payment"
      ? getPaymentStatusTone(value)
      : type === "fulfillment"
        ? getFulfillmentStatusTone(value)
        : getOrderStatusTone(value || fallback);

  return (
    <span className={`order-detail__badge order-detail__badge--${tone}`}>
      {label}
    </span>
  );
}

function OrderOverview({ order }) {
  const currency = getOrderCurrency(order);

  return (
    <section className="order-detail__overview" aria-label="Order overview">
      <div className="order-detail__overview-card">
        <span>Order status</span>
        <StatusBadge value={order.status} />
      </div>
      <div className="order-detail__overview-card">
        <span>Payment</span>
        <strong>{getPaymentLabel(order)}</strong>
        <StatusBadge
          value={order.payment?.status}
          fallback="Unpaid"
          type="payment"
        />
      </div>
      <div className="order-detail__overview-card">
        <span>Fulfilment</span>
        <strong>{humanize(order.fulfillment?.method, "Delivery")}</strong>
        <StatusBadge
          value={order.fulfillment?.status}
          fallback="Processing"
          type="fulfillment"
        />
      </div>
      <div className="order-detail__overview-card">
        <span>Total</span>
        <strong>{formatMoney(order.grandTotal, currency)}</strong>
      </div>
    </section>
  );
}

function OrderItems({ order }) {
  const currency = getOrderCurrency(order);
  const items = getOrderItems(order);

  return (
    <section className="order-detail__panel">
      <header className="order-detail__panel-header">
        <h2>Items</h2>
        <span>{items.length} line(s)</span>
      </header>

      <div className="order-detail__panel-body order-detail__items">
        {items.length ? (
          items.map((item) => (
            <article
              className="order-detail__item"
              key={item.id || item._id || `${item.productName}-${item.sku}`}
            >
              <div className="order-detail__item-image" aria-hidden="true">
                {item.image ? <img src={item.image} alt="" /> : null}
              </div>

              <div>
                <strong>{getLineTitle(item)}</strong>
                <small>{getLineMeta(item) || "Academy Costumes London"}</small>
                <small>Qty {getLineQuantity(item)}</small>
                <BuyAgainButton item={item} />
              </div>

              <div className="order-detail__item-total">
                <small>{formatMoney(item.unitPrice, currency)} each</small>
                {formatMoney(item.lineTotal, currency)}
              </div>
            </article>
          ))
        ) : (
          <p>No order items are available for this record.</p>
        )}
      </div>
    </section>
  );
}

function OrderSummary({ order }) {
  const currency = getOrderCurrency(order);

  return (
    <section className="order-detail__panel">
      <header className="order-detail__panel-header">
        <h2>Summary</h2>
      </header>

      <div className="order-detail__panel-body order-detail__summary">
        <div className="order-detail__summary-row">
          <span>Subtotal</span>
          <strong>{formatMoney(order.subtotal, currency)}</strong>
        </div>
        <div className="order-detail__summary-row">
          <span>Shipping</span>
          <strong>{formatMoney(order.shippingTotal, currency)}</strong>
        </div>
        <div className="order-detail__summary-row">
          <span>Tax</span>
          <strong>{formatMoney(order.taxTotal, currency)}</strong>
        </div>
        <div className="order-detail__summary-row">
          <span>
            {order.promoCode?.code
              ? `Discount (${order.promoCode.code})`
              : "Discount"}
          </span>
          <strong>-{formatMoney(order.discountTotal, currency)}</strong>
        </div>
        <div className="order-detail__summary-row order-detail__summary-row--total">
          <span>Total</span>
          <strong>{formatMoney(order.grandTotal, currency)}</strong>
        </div>
      </div>
    </section>
  );
}

function OrderFacts({ order }) {
  const address = getPrimaryAddress(order);
  const addressLines = [
    address.fullName,
    address.line1,
    address.line2,
    address.city,
    address.postalCode,
    address.country,
  ].filter(Boolean);

  return (
    <section className="order-detail__panel">
      <header className="order-detail__panel-header">
        <h2>Details</h2>
      </header>

      <div className="order-detail__panel-body order-detail__facts">
        <div className="order-detail__fact">
          <span>Payment</span>
          <strong>{getPaymentLabel(order)}</strong>
          <StatusBadge
            value={order.payment?.status}
            fallback="Unpaid"
            type="payment"
          />
        </div>

        <div className="order-detail__fact">
          <span>Fulfilment</span>
          <strong>{humanize(order.fulfillment?.method, "Delivery")}</strong>
          <StatusBadge
            value={order.fulfillment?.status}
            fallback="Processing"
            type="fulfillment"
          />
        </div>

        <div className="order-detail__fact">
          <span>Customer</span>
          <strong>{getCustomerName(order) || "Customer"}</strong>
          <p>{order.customer?.email || ""}</p>
        </div>

        <div className="order-detail__fact">
          <span>Address</span>
          <p>{addressLines.length ? addressLines.join(", ") : "Not provided"}</p>
        </div>

      </div>
    </section>
  );
}

export default function OrderScreen() {
  const { id = "" } = useParams();
  const dispatch = useDispatch();
  const details = useSelector((state) => state.order?.details || {});
  const order = details.order || null;
  const orderNumber = getOrderNumber(order) || safeText(id);

  const loadOrder = useCallback(async () => {
    const identifier = safeText(id);
    if (!identifier) return;

    try {
      if (isObjectId(identifier)) {
        await dispatch(getOrderById(identifier));
      } else {
        await dispatch(getOrderByNumber(identifier));
      }
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not load this order right now. Please try again later.",
        ),
      );
    }
  }, [dispatch, id]);

  useEffect(() => {
    loadOrder();

    return () => {
      dispatch(resetOrderError());
      dispatch(resetOrderMeta());
      dispatch(resetOrderDetails());
    };
  }, [dispatch, loadOrder]);

  const checkoutMessage = useMemo(
    () =>
      safeText(order?.customerCheckout?.message) ||
      "Review your order details and receipt.",
    [order],
  );

  if (details.loading && !order) {
    return (
      <main className="order-detail">
        <section className="order-detail__empty">
          <Loader2 className="order-detail__spin" />
          <p>Loading your order...</p>
        </section>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="order-detail">
        <Meta title="Order | Academy Costumes London" robots="noindex, nofollow" />
        <Link to="/orders" className="order-detail__back">
          <ArrowLeft size={18} />
          <span>Back to orders</span>
        </Link>
        <section className="order-detail__empty">
          <PackageCheck size={34} />
          <h1>Order unavailable</h1>
          <p>{details.error || "We could not find this order."}</p>
          <button type="button" className="order-detail__button" onClick={loadOrder}><RefreshCw size={16} /> Retry</button>
        </section>
      </main>
    );
  }

  return (
    <main className="order-detail">
      <Meta
        title={`Order ${orderNumber} | Academy Costumes London`}
        description="Review your Academy Costumes London order details."
        robots="noindex, nofollow"
      />

      <Link to="/orders" className="order-detail__back">
        <ArrowLeft size={18} />
        <span>Back to orders</span>
      </Link>

      <header className="order-detail__hero">
        <div>
          <p className="order-detail__eyebrow">Academy Costumes London order</p>
          <h1>{orderNumber}</h1>
          <p className="order-detail__subtitle">{checkoutMessage}</p>
        </div>

        <div className="order-detail__actions">
          <button
            type="button"
            className="order-detail__button"
            onClick={loadOrder}
            disabled={Boolean(details.loading)}
          >
            {details.loading ? (
              <Loader2 size={16} className="order-detail__spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            <span>Refresh</span>
          </button>
          <Link
            to={`/order/${encodeURIComponent(orderNumber)}/receipt`}
            className="order-detail__button order-detail__button--dark"
          >
            <FileText size={16} />
            <span>Receipt</span>
          </Link>
        </div>
      </header>

      <OrderOverview order={order} />
      <OrderTimeline order={order} />
      <Link className="order-detail__button" to={`/contact?orderNumber=${encodeURIComponent(orderNumber)}`}><MessageCircle size={16} /> Contact support</Link>
      {order.refund?.status && order.refund.status !== "not_refunded" ? <p role="status">Refund: {humanize(order.refund.status)}. {formatMoney(order.payment?.amountRefunded || 0, order.currency)} confirmed refunded.</p> : null}
      <CustomerOrderActions key={orderNumber} order={order} onRefresh={loadOrder} />

      <div className="order-detail__grid">
        <OrderItems order={order} />
        <div className="order-detail__facts">
          <OrderSummary order={order} />
          <OrderFacts order={order} />
        </div>
      </div>
    </main>
  );
}
